import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  Textarea,
  VStack,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Divider,
  Badge,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { axiosInstance } from '../../Utilities/networks';

const QuotationForm = () => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [calculation, setCalculation] = useState({
    subtotal: 0,
    gstAmount: 0,
    total: 0,
  });

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name must be less than 100 characters')
      .required('Name is required'),
    address: Yup.string()
      .min(10, 'Address must be at least 10 characters')
      .max(500, 'Address must be less than 500 characters')
      .required('Address is required'),
    model: Yup.string()
      .min(2, 'Model must be at least 2 characters')
      .required('Model is required'),
    basic_price: Yup.number()
      .positive('Basic price must be positive')
      .required('Basic price is required'),
    quantity: Yup.number()
      .integer('Quantity must be a whole number')
      .min(1, 'Quantity must be at least 1')
      .required('Quantity is required'),
    gst_rate: Yup.number()
      .min(0, 'GST rate cannot be negative')
      .max(100, 'GST rate cannot exceed 100%')
      .required('GST rate is required'),
    service_charges: Yup.number()
      .min(0, 'Service charges cannot be negative')
      .default(0),
    freight_charges: Yup.number()
      .min(0, 'Freight charges cannot be negative')
      .default(0),
  });

  // Calculate totals - pure function, no setState
  const calculateTotals = (values) => {
    const basic_price = parseFloat(values.basic_price) || 0;
    const quantity = parseInt(values.quantity) || 1;
    const gst_rate = parseFloat(values.gst_rate) || 0;
    const service_charges = parseFloat(values.service_charges) || 0;
    const freight_charges = parseFloat(values.freight_charges) || 0;

    const subtotal = basic_price * quantity;
    const gstAmount = subtotal * (gst_rate / 100);
    const total = subtotal + gstAmount + service_charges + freight_charges;

    return {
      subtotal: subtotal.toFixed(2),
      gstAmount: gstAmount.toFixed(2),
      total: total.toFixed(2),
      finalTotal: total
    };
  };

  const handleSubmit = React.useCallback(async (values, { resetForm }) => {
    setIsSubmitting(true);

    try {
      const totals = calculateTotals(values);

      const submissionData = {
        ...values,
        basic_price: parseFloat(values.basic_price),
        quantity: parseInt(values.quantity),
        gst_rate: parseFloat(values.gst_rate),
        service_charges: parseFloat(values.service_charges) || 0,
        freight_charges: parseFloat(values.freight_charges) || 0,
        total: totals.finalTotal,
      };

      const response = await axiosInstance.post('/api/sales/quotations', submissionData);

      if (response.data.status === 'success') {
        const quotationId = response.data.quotation_id;

        toast({
          title: 'Quotation Created Successfully!',
          description: `Quotation #${quotationId} has been created with total ₹${totals.finalTotal.toLocaleString('en-IN')}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Offer to download PDF
        setTimeout(() => {
          handleDownloadPDF(quotationId);
        }, 1000);

        resetForm();
        setCalculation({ subtotal: 0, gstAmount: 0, total: 0 });
      } else {
        throw new Error(response.data.message || 'Failed to create quotation');
      }
    } catch (error) {
      console.error('Error creating quotation:', error);

      toast({
        title: 'Creation Failed',
        description: error.response?.data?.message || error.message || 'Please try again later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [toast]);

  const handleDownloadPDF = async (quotationId) => {
    setIsDownloading(true);

    try {
      const response = await axiosInstance.get(`/api/sales/quotations/${quotationId}/pdf`, {
        responseType: 'blob', // Important for downloading files
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotation_${quotationId}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'PDF Downloaded Successfully!',
        description: `Quotation #${quotationId} PDF has been downloaded`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error downloading PDF:', error);

      toast({
        title: 'Download Failed',
        description: 'Failed to download PDF. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  // Component state for form values to avoid useMemo in callback
  const [formValues, setFormValues] = useState({
    name: '',
    address: '',
    model: '',
    basic_price: '',
    quantity: 1,
    gst_rate: 18,
    service_charges: 0,
    freight_charges: 0,
  });

  // Memoize totals calculation at component level
  const totals = React.useMemo(() => calculateTotals(formValues), [formValues]);

  return (
    <Container maxW="6xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Box textAlign="center">
          <Heading size="lg" color="blue.600" mb={2}>
            Create Quotation
          </Heading>
          <Text color="gray.600">
            Generate detailed quotations with automatic price calculations
          </Text>
        </Box>

        <Formik
          initialValues={formValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => {
            // Formik values will be synced when user interacts with form
            return (
              <Flex gap={6} direction={{ base: 'column', lg: 'row' }}>
                {/* Form Section */}
                <Box flex="1">
                  <Card shadow="lg" borderRadius="xl">
                    <CardHeader pb={4}>
                      <Heading size="md" color="gray.700">
                        Customer & Product Details
                      </Heading>
                    </CardHeader>
                    <CardBody>
                      <Form>
                        <VStack spacing={6}>
                          {/* Customer Name */}
                          <FormControl isInvalid={errors.name && touched.name}>
                            <FormLabel htmlFor="name" fontWeight="semibold">
                              Customer Name *
                            </FormLabel>
                            <Field
                              as={Input}
                              id="name"
                              name="name"
                              placeholder="Enter customer name"
                              size="lg"
                              focusBorderColor="blue.400"
                            />
                            <FormErrorMessage>{errors.name}</FormErrorMessage>
                          </FormControl>

                          {/* Customer Address */}
                          <FormControl isInvalid={errors.address && touched.address}>
                            <FormLabel htmlFor="address" fontWeight="semibold">
                              Address *
                            </FormLabel>
                            <Field
                              as={Textarea}
                              id="address"
                              name="address"
                              placeholder="Enter complete address"
                              size="lg"
                              rows={3}
                              resize="vertical"
                              focusBorderColor="blue.400"
                            />
                            <FormErrorMessage>{errors.address}</FormErrorMessage>
                          </FormControl>

                          {/* Product Model */}
                          <FormControl isInvalid={errors.model && touched.model}>
                            <FormLabel htmlFor="model" fontWeight="semibold">
                              Product Model *
                            </FormLabel>
                            <Field
                              as={Input}
                              id="model"
                              name="model"
                              placeholder="Enter product model"
                              size="lg"
                              focusBorderColor="blue.400"
                            />
                            <FormErrorMessage>{errors.model}</FormErrorMessage>
                          </FormControl>

                          <Divider />

                          {/* Pricing Section */}
                          <Box w="full">
                            <Heading size="sm" color="gray.700" mb={4}>
                              Pricing Details
                            </Heading>

                            {/* Basic Price & Quantity */}
                            <HStack spacing={4} mb={4}>
                              <FormControl isInvalid={errors.basic_price && touched.basic_price}>
                                <FormLabel htmlFor="basic_price" fontWeight="semibold">
                                  Basic Price (₹) *
                                </FormLabel>
                                <Field name="basic_price">
                                  {({ field }) => (
                                    <NumberInput
                                      {...field}
                                      min={0}
                                      precision={2}
                                      size="lg"
                                      focusBorderColor="blue.400"
                                      onChange={(value) => setFieldValue('basic_price', value)}
                                    >
                                      <NumberInputField placeholder="0.00" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  )}
                                </Field>
                                <FormErrorMessage>{errors.basic_price}</FormErrorMessage>
                              </FormControl>

                              <FormControl isInvalid={errors.quantity && touched.quantity}>
                                <FormLabel htmlFor="quantity" fontWeight="semibold">
                                  Quantity *
                                </FormLabel>
                                <Field name="quantity">
                                  {({ field }) => (
                                    <NumberInput
                                      {...field}
                                      min={1}
                                      precision={0}
                                      size="lg"
                                      focusBorderColor="blue.400"
                                      onChange={(value) => setFieldValue('quantity', value)}
                                    >
                                      <NumberInputField placeholder="1" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  )}
                                </Field>
                                <FormErrorMessage>{errors.quantity}</FormErrorMessage>
                              </FormControl>
                            </HStack>

                            {/* GST Rate */}
                            <FormControl isInvalid={errors.gst_rate && touched.gst_rate} mb={4}>
                              <FormLabel htmlFor="gst_rate" fontWeight="semibold">
                                GST Rate (%) *
                              </FormLabel>
                              <Field name="gst_rate">
                                {({ field }) => (
                                  <NumberInput
                                    {...field}
                                    min={0}
                                    max={100}
                                    precision={2}
                                    size="lg"
                                    focusBorderColor="blue.400"
                                    onChange={(value) => setFieldValue('gst_rate', value)}
                                  >
                                    <NumberInputField placeholder="18.00" />
                                    <NumberInputStepper>
                                      <NumberIncrementStepper />
                                      <NumberDecrementStepper />
                                    </NumberInputStepper>
                                  </NumberInput>
                                )}
                              </Field>
                              <FormErrorMessage>{errors.gst_rate}</FormErrorMessage>
                            </FormControl>

                            {/* Service & Freight Charges */}
                            <HStack spacing={4}>
                              <FormControl isInvalid={errors.service_charges && touched.service_charges}>
                                <FormLabel htmlFor="service_charges" fontWeight="semibold">
                                  Service Charges (₹)
                                </FormLabel>
                                <Field name="service_charges">
                                  {({ field }) => (
                                    <NumberInput
                                      {...field}
                                      min={0}
                                      precision={2}
                                      size="lg"
                                      focusBorderColor="blue.400"
                                      onChange={(value) => setFieldValue('service_charges', value)}
                                    >
                                      <NumberInputField placeholder="0.00" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  )}
                                </Field>
                                <FormErrorMessage>{errors.service_charges}</FormErrorMessage>
                              </FormControl>

                              <FormControl isInvalid={errors.freight_charges && touched.freight_charges}>
                                <FormLabel htmlFor="freight_charges" fontWeight="semibold">
                                  Freight Charges (₹)
                                </FormLabel>
                                <Field name="freight_charges">
                                  {({ field }) => (
                                    <NumberInput
                                      {...field}
                                      min={0}
                                      precision={2}
                                      size="lg"
                                      focusBorderColor="blue.400"
                                      onChange={(value) => setFieldValue('freight_charges', value)}
                                    >
                                      <NumberInputField placeholder="0.00" />
                                      <NumberInputStepper>
                                        <NumberIncrementStepper />
                                        <NumberDecrementStepper />
                                      </NumberInputStepper>
                                    </NumberInput>
                                  )}
                                </Field>
                                <FormErrorMessage>{errors.freight_charges}</FormErrorMessage>
                              </FormControl>
                            </HStack>
                          </Box>

                          {/* Submit Button */}
                          <Button
                            type="submit"
                            colorScheme="blue"
                            size="lg"
                            width="full"
                            isLoading={isSubmitting}
                            loadingText="Creating Quotation..."
                            disabled={isSubmitting}
                            _hover={{
                              bg: 'blue.600',
                              transform: 'translateY(-1px)',
                            }}
                            transition="all 0.2s"
                          >
                            Create Quotation
                          </Button>
                        </VStack>
                      </Form>
                    </CardBody>
                  </Card>
                </Box>

                {/* Calculation Summary */}
                <Box flex="0 0 300px">
                  <Card shadow="lg" borderRadius="xl" position="sticky" top={4}>
                    <CardHeader pb={4}>
                      <Heading size="md" color="gray.700">
                        Price Summary
                      </Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <Box>
                          <HStack justify="space-between" mb={2}>
                            <Text>Subtotal:</Text>
                            <Text fontWeight="semibold">₹{totals.subtotal}</Text>
                          </HStack>
                          <HStack justify="space-between" mb={2}>
                            <Text>GST ({values.gst_rate || 0}%):</Text>
                            <Text fontWeight="semibold">₹{totals.gstAmount}</Text>
                          </HStack>
                          <HStack justify="space-between" mb={2}>
                            <Text>Service Charges:</Text>
                            <Text fontWeight="semibold">₹{parseFloat(values.service_charges || 0).toFixed(2)}</Text>
                          </HStack>
                          <HStack justify="space-between" mb={3}>
                            <Text>Freight Charges:</Text>
                            <Text fontWeight="semibold">₹{parseFloat(values.freight_charges || 0).toFixed(2)}</Text>
                          </HStack>
                          <Divider />
                          <HStack justify="space-between" mt={3}>
                            <Text fontSize="lg" fontWeight="bold">Total:</Text>
                            <Text fontSize="lg" fontWeight="bold" color="green.500">
                              ₹{totals.total}
                            </Text>
                          </HStack>
                        </Box>

                        <Alert status="info" borderRadius="md">
                          <AlertIcon />
                          <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>
                              Auto-calculation
                            </Text>
                            <Text fontSize="xs">
                              All calculations update automatically as you modify the values above.
                            </Text>
                          </Box>
                        </Alert>

                        <Button
                          colorScheme="blue"
                          size="md"
                          width="full"
                          isLoading={isDownloading}
                          loadingText="Generating PDF..."
                          disabled={isDownloading}
                          onClick={() => {
                            // This will be enabled when we have the quotation ID
                            toast({
                              title: 'PDF Download',
                              description: 'PDF will be automatically downloaded after creating the quotation',
                              status: 'info',
                              duration: 3000,
                              isClosable: true,
                            });
                          }}
                          _hover={{
                            bg: 'blue.600',
                            transform: 'translateY(-1px)',
                          }}
                          transition="all 0.2s"
                        >
                          📄 Download PDF (After Creation)
                        </Button>
                      </VStack>
                    </CardBody>
                  </Card>
                </Box>
              </Flex>
            );
          }}
        </Formik>
      </VStack>
    </Container>
  );
};

export default QuotationForm;
